import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { AuthError, BrowserAuthErrorMessage } from "@azure/msal-browser";
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { catchError, filter, from, map, of, switchMap, tap } from "rxjs";
import { AuthenticationActions } from "./action-types";
import { AuthenticationService } from "./authentication.service";
import { PortoAuthenticationService } from "./porto-authentication.service";

@Injectable()
export class AuthenticationEffects implements OnInitEffects {
    initialwindow = window.location.href
    readonly init$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthenticationActions.init),
            switchMap(() => {
                return this.auth.checkSession().pipe(
                    map(user => {
                        if (user) return this.getAuthResult(user, this.initialwindow)
                        else return AuthenticationActions.signIn({ landingUrl: window.location.href })
                    }),
                    catchError(error => {
                        if (error?.name == 'TimeoutError') return of(AuthenticationActions.signIn({ landingUrl: window.location.href }))
                        return of(AuthenticationActions.signInFailed({ error }))
                    })
                );
            })
        )
    )

    readonly signIn$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthenticationActions.signIn),
            switchMap(action => {
                if (window.self !== window.top) {
                    return this.auth.loginWithSSO().pipe(
                        map(user => {
                            if (user) {
                                return this.getAuthResult(user, action.landingUrl)
                            } else {
                                return AuthenticationActions.signInFailed({ error: { name: 'InteractionRequiredAuthError', message: 'User interaction required' } })
                            }
                        }),
                        catchError(error => of(AuthenticationActions.signInFailed({ error })))
                    )
                } else {
                    return from(this.auth.loginWithRedirect({ target: action.landingUrl })).pipe(
                        switchMap(() =>
                            this.auth.checkSession().pipe(
                                map(user => this.getAuthResult(user, action.landingUrl)),
                                catchError(error => {
                                    if (error?.name === 'TimeoutError') {
                                        return of(AuthenticationActions.signIn({ landingUrl: window.location.href }))
                                    }
                                    return of(AuthenticationActions.signInFailed({ error }))
                                })
                            )
                        )
                    )
                }
            })
        )
    )

    readonly signOut$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthenticationActions.signOut),
            switchMap(_ =>
                this.auth.logoutWithRedirect().pipe(
                    //tap(action => { debugger; this.auth.clearInteractionToken() }),
                    map(() => AuthenticationActions.signedOut()),
                    catchError((error) => of(AuthenticationActions.signInFailed({ error })))
                )
            )
        )
    )

    readonly failed$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthenticationActions.signInFailed),
            filter(action => action.error instanceof AuthError),
            map(action => action.error as AuthError),
            filter(AuthenticationEffects.isInteractionInProgress),
            map(() => AuthenticationActions.signOut())
        )
    )

    readonly logInPorto$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthenticationActions.signedIn),
            switchMap(action => this.portoAuthenticationService.login$())
        ), { dispatch: false }
    )

    readonly signedIn$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AuthenticationActions.signedIn),
            tap(action => {
                if (action.redirectToUrl) {
                    const url = new URL(action.redirectToUrl)
                    this.router.navigateByUrl(url.toString().substring(url.origin.length).substring(2))
                }
            })
        ), { dispatch: false }
    )

    constructor(
        private readonly actions$: Actions,
        private readonly auth: AuthenticationService,
        private readonly router: Router,
        private readonly portoAuthenticationService: PortoAuthenticationService
    ) { }

    ngrxOnInitEffects() {
        return AuthenticationActions.init();
    }

    private static isInteractionInProgress(error: AuthError) {
        return error.errorCode === BrowserAuthErrorMessage.interactionInProgress.code;
    }

    private getAuthResult(user: { sub: string } | undefined, redirectToUrl: string | undefined = undefined) {
        if (user) {
            return AuthenticationActions.signedIn({ user, redirectToUrl });
        } else {
            return AuthenticationActions.signedOut();
        }
    }




}